import { ReloadOutlined, SettingOutlined } from "@ant-design/icons";
import { useQueryClient } from "@tanstack/react-query";
import { Skeleton, Table, Popover, Checkbox } from "antd";
import React, { useCallback, useState } from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import "../../pages/Dashboard/style.css";
import { useSelector } from "react-redux";
import { RootDiv, StyledTheme } from "./style";

function DashboardCommonTable(props) {
  const {
    tradeQuery,
    dataSource,
    isLoading,
    queryKey,
    columns,
    onChange,
    emptyPage,
    handleEdit,
    handleExecute,
    deleteTradeLoading,
    handleDeleteTrade,
  } = props;

  const [showAction, setShowAction] = useState({ flag: false, index: null });
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tableRef = useRef(null);
  const lastRowRef = useRef(null);
  const scrollRef = useRef(null);

  //redux
  const loginUserdetails = useSelector((state) => state?.authSlice?.userDetail);
  const loginUserId = loginUserdetails?.userId;

  const handleObserver = useCallback(
    (entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          tradeQuery?.hasNextPage &&
          !tradeQuery.isFetchingNextPage
        ) {
          tradeQuery?.fetchNextPage();
        }
      });
    },
    [
      tradeQuery?.fetchNextPage,
      tradeQuery?.hasNextPage,
      tradeQuery?.isFetchingNextPage,
    ]
  );

  // life cycle
  // useEffect(() => {
  //   const handleTableWheelScroll = (e) => {
  //     const tableBody = scrollRef.current?.querySelector(".ant-table-body");

  //     if (tableBody && tableBody.contains(e.target)) {
  //       const scrollTop = tableBody.scrollTop;
  //       const maxScrollTop = tableBody.scrollHeight - tableBody.clientHeight;

  //       // Prevent default scrolling only when table can scroll further
  //       if (
  //         (e.deltaY < 0 && scrollTop > 0) || // Scroll up when not at the top
  //         (e.deltaY > 0 && scrollTop < maxScrollTop) // Scroll down when not at the bottom
  //       ) {
  //         e.preventDefault();
  //         tableBody.scrollTop += e.deltaY * 2; // Manually scroll the table
  //       }
  //     }
  //   };

  //   // Attach event listener to table body for mouse scroll
  //   const tableBody = scrollRef.current?.querySelector(".ant-table-body");
  //   if (tableBody) {
  //     tableBody.addEventListener("wheel", handleTableWheelScroll, {
  //       passive: false, // Allow preventDefault to work
  //     });
  //   }

  //   // Cleanup the event listener on unmount
  //   return () => {
  //     if (tableBody) {
  //       tableBody.removeEventListener("wheel", handleTableWheelScroll);
  //     }
  //   };
  // }, []);

  useEffect(() => {
    // Only set up the observer if we have data and there's a next page to fetch
    if (!dataSource?.length || !tradeQuery?.hasNextPage) return;

    const observer = new IntersectionObserver(handleObserver, {
      // Using null instead of tableRef.current to observe relative to the viewport
      root: null,
      // Increase the bottom margin to trigger loading only when really close to the end
      rootMargin: "0px 0px 100px 0px",
      // Require more visibility before triggering (0.5 means 50% of the element must be visible)
      threshold: 0.5,
    });

    // For desktop view with Table component
    const rows = tableRef.current?.querySelectorAll(".ant-table-row");

    // Only observe the very last row
    if (rows && rows.length > 0) {
      const lastRow = rows[rows.length - 1];
      observer.observe(lastRow);
      lastRowRef.current = lastRow;
    }

    return () => {
      if (lastRowRef.current) {
        observer.unobserve(lastRowRef.current);
        lastRowRef.current = null;
      }
    };
  }, [dataSource, tradeQuery?.hasNextPage]);

  // function
  const handleReload = () => {
    queryClient.resetQueries({ queryKey: [props?.queryKey] });
  };
  const navigator = () => {
    switch (queryKey) {
      case "allTrade":
        navigate("/trading/trade");
        break;
      case "pendingTrade":
        navigate(`/trading/trade?userId=${loginUserId}&tradeStatus=pending`);
        break;
      case "rejectionLogs":
        navigate("/utilities/rejection-log");
        break;
      case "m2mAlerts":
        navigate("/utilities/M2mAlert-Log");
        break;
    }
  };

  const handleAction = (index) => {
    if (queryKey === "pendingTrade") {
      setShowAction({ flag: !showAction.flag, index: index });
    }
  };

  const handleColumnVisibility = (checkedValues) => {
    const allKeys = columns.map(c => c.key || c.dataIndex).filter(Boolean);
    const hidden = allKeys.filter(k => !checkedValues.includes(k));
    setHiddenColumns(hidden);
  };

  const visibleColumns = columns.filter(col => {
    const key = col.key || col.dataIndex;
    if (!key) return true;
    return !hiddenColumns.includes(key);
  });

  const columnOptions = columns
    .filter(col => col.key || col.dataIndex)
    .map(col => {
      let label = col.title;
      if (!label) {
        if (col.key === 'index' || col.dataIndex === 'index') label = 'Device';
        else if (col.key === 'edit' || col.dataIndex === 'edit') label = 'Action';
        else if (col.key === 'action' || col.dataIndex === 'action') label = 'Action';
        else label = col.key || col.dataIndex;
      }
      return {
        label,
        value: col.key || col.dataIndex
      };
    });

  const popoverContent = (
    <Checkbox.Group
      options={columnOptions}
      value={columnOptions.map(c => c.value).filter(k => !hiddenColumns.includes(k))}
      onChange={handleColumnVisibility}
      style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '300px', overflowY: 'auto' }}
    />
  );

  return (
    <RootDiv ref={scrollRef} querykey={queryKey}>
      <StyledTheme.MainHeader>
        <StyledTheme.Title tableTitle={props && props?.tableTitle}>
          {props && props?.tableTitle}
        </StyledTheme.Title>
        <StyledTheme.RightHeaderWrapper>
          {props?.allowBtn ? (
            <StyledTheme.AllowButton block type="primary" htmlType="submit">
              Allow
            </StyledTheme.AllowButton>
          ) : null}
          <StyledTheme.ViewMore onClick={navigator}>
            {props?.viewMore ? "View More>>" : ""}
          </StyledTheme.ViewMore>
          <Popover content={popoverContent} title="Visible Columns" trigger="click" placement="bottomRight">
            <StyledTheme.ReloadSpan style={{ marginRight: 0 }}>
              <SettingOutlined style={{ cursor: "pointer" }} title="Columns" />
            </StyledTheme.ReloadSpan>
          </Popover>
          <StyledTheme.ReloadSpan onClick={handleReload}>
            <ReloadOutlined style={{ cursor: "pointer" }} />
          </StyledTheme.ReloadSpan>
        </StyledTheme.RightHeaderWrapper>
      </StyledTheme.MainHeader>

      <StyledTheme.TableWrapper ref={tableRef}>
        <StyledTheme.Table
          // virtual
          columns={visibleColumns}
          dataSource={dataSource || []}
          pagination={false}
          onChange={onChange}
          sticky
          locale={{
            emptyText: isLoading ? <Skeleton active /> : undefined,
          }}
          rowKey="_id"
          summary={() => {
            return (
              tradeQuery?.isFetchingNextPage && (
                <Table.Summary.Row id={`loader-target${queryKey}`}>
                  <Table.Summary.Cell index={0} colSpan={columns.length}>
                    <Skeleton active paragraph={{ rows: 2 }} title={false} />
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )
            );
          }}
          scroll={{
            x:
              isLoading || dataSource?.length === 0 ? undefined : "max-content",
            y: 225,
          }}
        />
        {/* <div style={{ textAlign: "center", margin: "10px 0" }}>
          {tradeQuery?.isFetchingNextPage && <Spin />}
        </div> */}
      </StyledTheme.TableWrapper>
    </RootDiv>
  );
}

export default React.memo(DashboardCommonTable); //DashboardCommonTable;
